import type {
  ElementType,
  MouseEventHandler,
  ReactNode,
  Ref,
  SyntheticEvent,
} from 'react';
import { createContext, use, useEffect, useRef } from 'react';
import { motion } from 'motion/react';

import CloseIcon from '../icons/CloseIcon';

import { useAriaId } from '../../hooks/use-aria-id';
import type { BoxProps } from '../box/box';
import { Box } from '../box/box';
import { Button } from '../button/button';
import { Card } from '../card/card';
import { Divider } from '../divider/divider';
import type { HeadingProps } from '../heading/heading';
import { Heading } from '../heading/heading';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';
import { Tabs } from '../tabs/tabs';
import { Text } from '../text/text';

import * as styles from './drawer.css';

interface DrawerCompoundProps {
  children: ReactNode;
}

interface DrawerHeadingProps extends HeadingProps {
  children: ReactNode;
}

interface DrawerContentProps {
  children: ReactNode;
  spacing?: BoxProps['spacing'];
}

export interface DrawerDisplayFieldProps {
  label: ReactNode | string;
  children: ReactNode;
}

type AutoOrManualClose =
  | {
      Component: ElementType;
      method: 'POST' | 'PATCH' | 'GET';
    }
  | {
      Component?: never;
      method?: 'dialog';
    };

export type DrawerProps = AutoOrManualClose & {
  /**
   * Function to close the drawer via outside state
   */
  onClose: () => void;
  onSubmit?: (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => void | false;
  /**
   * Function to render a hidden submit button, this is used to trigger the
   * submit event when a user presses 'enter' on a form. This may need custom
   * elements when we want to show a confirm dialog
   */
  keyboardSubmitButton?: ReactNode;
  /**
   * Text to assist users with screen readers when the close button is focussed
   */
  closeDialogText: ReactNode;
  open?: boolean;
  children: ReactNode;
  ref?: Ref<HTMLFormElement>;
  Component?: ElementType;
  method?: 'dialog' | 'POST' | 'PATCH' | 'GET';
  /**
   * Remix specific props for page reload on form submission,
   * if no navigation is triggered use fetcher a key to provide access to the fetcher
   */
  navigate?: boolean;
  fetcherKey?: string;
};

const drawerSpacing = {
  paddingX: 'basePos3',
  paddingY: 'basePos4',
  gap: 'basePos3',
} as const;

const IdContext = createContext('');

const DrawerHeader = ({ children }: DrawerCompoundProps) => {
  return (
    <Box
      className={styles.header}
      padding={{ x: drawerSpacing.paddingX, bottom: drawerSpacing.gap }}
      spacing={drawerSpacing.gap}
    >
      {children}
    </Box>
  );
};

const DrawerSummary = ({ children }: DrawerCompoundProps) => {
  return (
    <Box className={styles.summary} stack="row" spacing={drawerSpacing.gap}>
      {children}
    </Box>
  );
};

/**
 * Gives the drawer an accessible title - to be used inside <Drawer.Header />
 */
const DrawerTitle = ({ children, variant = 'title' }: DrawerHeadingProps) => {
  const id = use(IdContext);

  return (
    <Heading id={id} variant={variant}>
      {children}
    </Heading>
  );
};

/**
 * For use inside <Drawer />, contains the main content of the drawer
 */
const DrawerContent = ({ children, spacing }: DrawerContentProps) => (
  <Box
    className={styles.content}
    padding={{ x: drawerSpacing.paddingX, y: drawerSpacing.paddingY }}
    spacing={spacing}
  >
    {children}
  </Box>
);

/**
 * For use inside <Drawer />,
 */
const DrawerTabs = ({ children }: DrawerCompoundProps) => (
  <Tabs className={styles.tabs} paddingX="basePos3">
    {children}
  </Tabs>
);

/**
 * For use inside <Drawer />,
 */
const DrawerDivider = () => <Divider color="neutral150" />;

export const DrawerDisplayField = ({
  label,
  children,
}: DrawerDisplayFieldProps) => {
  return (
    <Box spacing="baseNeg4" alignContent={{ x: 'left' }}>
      <Text variant="captionSmall" color="neutral400" transform="uppercase">
        {label}
      </Text>
      {children}
    </Box>
  );
};

/**
 * For use between content and actions to contain validation messages
 */
const Information = ({ children }: DrawerCompoundProps) => (
  <Box padding={{ x: drawerSpacing.paddingX }} className={styles.validation}>
    {children}
  </Box>
);

/**
 * For use as final child of <Drawer /> to contain all action buttons
 */
const DrawerActions = ({ children }: DrawerCompoundProps) => (
  <Box
    stack="row"
    spacing="basePos3"
    padding={{ x: drawerSpacing.paddingX, top: 'basePos4' }}
    className={styles.actions}
  >
    {children}
  </Box>
);

/**
 * For use inside <DrawerActions />, closes the dialog using the form
 */
const DrawerCloseButton = ({ children }: DrawerCompoundProps) => (
  <Button type="submit" value="close" variant="outline">
    {children}
  </Button>
);

const hiddenSubmit = <button type="submit" style={{ display: 'none' }} />;

export const Drawer = ({
  children,
  open = true,
  onClose,
  closeDialogText,
  ref,
  Component = 'form',
  method = 'dialog',
  onSubmit,
  /** This hidden button is used to submit the form if a user presses 'enter' on a field
   *  If we did not have this and the first submit button is a `cancel` button the form would not submit
   */
  keyboardSubmitButton = hiddenSubmit,
  ...formProps
}: DrawerProps) => {
  // The headingId links the drawer with the heading for good a11y
  const headingId = useAriaId('drawer-heading');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (open) dialogRef.current?.showModal();

    // We need to track if outside state is declaring the drawer closed, if so we
    // need to manually close the html dialog if open
    if (!open && dialogRef.current?.open) dialogRef.current?.close();
  }, [open]);

  const closeOnOutsideClick:
    | MouseEventHandler<HTMLDialogElement>
    | undefined = ({ currentTarget, target }) => {
    const hasClickedOutsideDrawer =
      target === currentTarget && target === dialogRef.current;

    if (hasClickedOutsideDrawer && dialogRef.current) {
      dialogRef.current.close();
    }
  };

  return (
    <IdContext value={headingId}>
      <motion.dialog
        aria-labelledby={headingId}
        className={styles.dialog}
        initial={{ translateX: '100%' }}
        animate={open ? { translateX: 0 } : { translateX: '100%' }}
        exit={{ translateX: '100%' }}
        transition={{
          mass: 1,
          stiffness: 150,
          damping: 18,
          velocity: 0.001,
        }}
        ref={dialogRef}
        onClick={closeOnOutsideClick}
        onClose={onClose}
      >
        <Card
          {...formProps}
          Component={Component}
          method={method}
          onSubmit={(e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
            const submitter = e.nativeEvent.submitter;

            const submitterValue =
              (submitter &&
                'value' in submitter &&
                typeof submitter.value === 'string' &&
                submitter.value) ||
              '';

            // Handle the dialog/form close actions
            if (submitterValue === 'close' || submitterValue === 'cancel') {
              // If not a dialog, close and prevent submission
              if (method !== 'dialog') {
                e.preventDefault();
                dialogRef.current?.close();
              }

              return;
            }

            return onSubmit?.(e);
          }}
          variant="darkShadow"
          padding={{ y: 'basePos4' }} // We add x padding in the compound components to allow the tabs to fit full-width
          className={styles.drawer}
          ref={ref}
        >
          {/* hidden submit button to handle keyboard submission. This must come after the 
          input fields so that it is the next submit item in the DOM when pressing enter */}
          <ScreenReaderText>{keyboardSubmitButton}</ScreenReaderText>

          {children}
          <Button
            className={styles.close}
            variant="icon"
            value="cancel"
            Icon={CloseIcon}
            iconColor="neutral500"
            size="medium"
            {...(method === 'dialog'
              ? {
                  type: 'submit',
                }
              : {
                  type: 'button',
                  onClick: onClose,
                })}
          >
            {closeDialogText}
          </Button>
        </Card>
      </motion.dialog>
    </IdContext>
  );
};

Drawer.Actions = DrawerActions;
Drawer.Header = DrawerHeader;
Drawer.Summary = DrawerSummary;
Drawer.Title = DrawerTitle;
Drawer.CloseButton = DrawerCloseButton;
Drawer.Content = DrawerContent;
Drawer.DisplayField = DrawerDisplayField;
Drawer.Tabs = DrawerTabs;
Drawer.TabsItem = Tabs.Item;
Drawer.Information = Information;
Drawer.Divider = DrawerDivider;
